import {ref} from "vue";
import {DateTime} from "luxon";
import {useHelpers} from "@/Composables";
import axios from "axios";

export default function useShipping() {
    const route = window.route;
    
    const isLoading = ref(false);
    const shipments = ref([]);

    const fetchTrackingInformation = (trackingNumbers) => {
        isLoading.value = true;
        
        // Store the current shipments data to avoid overwriting if API fails
        const currentShipments = [...shipments.value];
        
        axios.post(route('api.v1.public.track_shipments'), {
            tracking_numbers: trackingNumbers
        })
        .then(response => {
            // API returns an object keyed by tracking number, convert to array
            if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
                const shipmentsArray = Object.values(response.data);
                if (shipmentsArray.length > 0) {
                    shipments.value = shipmentsArray;
                }
            }
            // Only update if we get valid tracking data (should be array)
            else if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                shipments.value = response.data;
            }
        })
        .catch(error => {
            console.error('Error fetching tracking information:', error);
            // Restore the original shipments data on error
            shipments.value = currentShipments;
        })
        .finally(() => {
            isLoading.value = false;
        });
    };

    function getDeliveryStatusHeader(status, estimatedDelivery, deliveryDate) {
        const s = status?.toLowerCase() || "";

        // Helper function to check if the time is exactly midnight
        const isEndOfDay = (isoDate) => {
            const parsedDate = DateTime.fromISO(isoDate, { setZone: true });
            return parsedDate.hour === 0 && parsedDate.minute === 0 && parsedDate.second === 0;
        };

        // Determine the text for estimated delivery
        const estimatedText = estimatedDelivery
            ? isEndOfDay(estimatedDelivery)
                ? `On the way - Estimated delivery by the end of day ${DateTime.fromISO(estimatedDelivery, { setZone: true }).toFormat('DDD')}`
                : `On the way - Estimated delivery on ${DateTime.fromISO(estimatedDelivery, { setZone: true }).toFormat('DDD, t')}`
            : "On the way - Delivery date to be determined";

        const deliveredText = deliveryDate
            ? `Delivered on ${DateTime.fromISO(deliveryDate, { setZone: true }).toFormat('DDD')} at ${DateTime.fromISO(deliveryDate, { setZone: true }).toFormat('h:mma')}`
            : "Delivered";

        const delayedText = "Your shipment is delayed";

        /*******************************************************
         * 1. PARTIAL MATCH FOR ANY STRING THAT CONTAINS "DELIVERED"
         *    (e.g. "delivered at facility", "delivered to a UPS Access Point")
         ********************************************************/
        if (s.includes("delivered")) {
            return {
                text: deliveredText,
                icon: "ShipStatusDelivered",
            };
        }

        if (s.includes("delay") || s.includes("delayed")) {
            return {
                text: delayedText,
                icon: "ShipStatusException",
            };
        }

        /*******************************************************
         * 2. SWITCH STATEMENT FOR EXACT MATCHES
         *    (UPS, FedEx, DHL, or generic)
         ********************************************************/
        switch (s) {
            /****************** FEDEX ******************/
            case "shipment information sent to fedex":
            case "label created": // FedEx or UPS
                return {
                    text: "Shipment Created",
                    icon: "ShipStatusLabelCreated",
                };

            case "in transit":
            case "on the way":
            case "shipped/on the way": // UPS variant
                return {
                    text: estimatedText,
                    icon: "ShipStatusInTransit",
                };

            case "at a local fedex facility":
                return {
                    text: "At Local Facility",
                    icon: "ShipStatusInTransit",
                };

            case "out for delivery":
                return {
                    text: "Out for Delivery",
                    icon: "ShipStatusInTransit",
                };

            case "scheduled delivery is now pending":
                return {
                    text: "Delivery Pending",
                    icon: "ShipStatusInTransit",
                };

            case "cancelled":
                return {
                    text: "Cancelled",
                    icon: "ShipStatusException",
                };

            case "delivery exception":
            case "exception": // UPS uses "Exception"
                return {
                    text: "Delivery Issue",
                    icon: "ShipStatusException",
                };

            case "delivery is held, unable to collect payment":
                return {
                    text: "Payment Issue",
                    icon: "ShipStatusException",
                };

            // Various "delay" statuses consolidated to "Delayed"
            case "clearance delay":
            case "operational delay":
            case "weather delay":
            case "local delay":
                return {
                    text: "Delayed",
                    icon: "ShipStatusException",
                };

            /****************** UPS ******************/
            case "transferred to post office for delivery":
                return {
                    text: "Transferred to Post Office",
                    icon: "ShipStatusInTransit",
                };

            case "preparing for delivery":
                return {
                    text: "Preparing for Delivery",
                    icon: "ShipStatusInTransit",
                }
            /****************** DHL ******************/
            // -- Informational (No Action Required) --
            case "shipment information received":
                return {
                    text: "Label Created (DHL)",
                    icon: "ShipStatusLabelCreated",
                };
            case "supplementary information has been received and merged with applicable shipment data":
                return {
                    text: "Shipment Data Updated",
                    icon: "ShipStatusInTransit",
                };
            case "arrived at sort facility":
            case "arrived at delivery facility":
            case "departed facility":
            case "transfer through":
            case "scheduled to depart on the next planned movement":
            case "payment is received and recorded for shipment related fees":
            case "clearance processing complete":
                return {
                    text: "In Transit",
                    icon: "ShipStatusInTransit",
                };
            case "shipment is out with courier for delivery":
            case "shipment out for delivery":
            case "scheduled for delivery":
                return {
                    text: "Out for Delivery",
                    icon: "ShipStatusInTransit",
                };

            // -- Notification Status (Optional) --
            case "shipment picked up":
            case "broker has been notified to arrange clearance":
            case "processed for clearance":
            case "shipment will be cleared and delivered by broker":
            case "delivery has been arranged through a third-party service. delivery details are not expected":
                return {
                    text: "In Transit",
                    icon: "ShipStatusInTransit",
                };

            // -- Alert Status (Immediate Attention) --
            case "awaiting collection by consignee":
                return {
                    text: "Awaiting Pickup",
                    icon: "ShipStatusException",
                };
            case "shipment is on hold":
                return {
                    text: "On Hold",
                    icon: "ShipStatusException",
                };
            case "delivery attempt could not be completed":
            case "delivery attempted but no response at consignee address":
                return {
                    text: "Delivery Attempt Failed",
                    icon: "ShipStatusException",
                };
            case "returned to shipper":
            case "returned to sender":
                return {
                    text: "Returned",
                    icon: "ShipStatusException",
                };
            case "further consignee information needed":
                return {
                    text: "Needs More Info",
                    icon: "ShipStatusException",
                };
            case "on hold awaiting for payment of shipment related fees":
                return {
                    text: "On Hold - Payment Required",
                    icon: "ShipStatusException",
                };

            /****************** Default / Unknown ******************/
            default:
                return {
                    text: "Details available at carrier tracking site",
                    icon: "ShipStatusInTransit",
                };
        }
    }

    function getPercentageComplete(shipment, order) {
        // If package is already delivered, show progress at the far right
        if (shipment.status?.text?.toLowerCase() === 'delivered') {
            return 'right';
        }

        // If we have no "shipped" date, we can't meaningfully calculate progress
        //    so just pin the progress to the far left.
        let targetShipment = order?.shipments?.data?.length
            ? order?.shipments?.data?.find(ship => ship.tracking_number === shipment.tracking_number)
            : order?.shipments?.find(ship => ship.tracking_number === shipment.tracking_number) ?? null;

        const dateShipped = targetShipment?.date;
        if (!dateShipped) {
            return 'left';
        }

        // Determine which date is your "target finish":
        //    - Prefer the estimated delivery if it exists.
        //    - Otherwise, fall back to the final delivery date (if any).
        const finalDate = shipment.estimated_delivery ?? shipment.delivery_date;
        if (!finalDate) {
            return 'left';
        }

        // 4. Sort the shipment events by date so we can find the earliest event
        const events = Array.isArray(shipment.events) ? shipment.events : [];
        events.sort((a, b) => new Date(a.date) - new Date(b.date));

        // 5. Use the first event's date as the "start" if any events exist.
        //    Otherwise, use `dateShipped`.
        const startDate = events.length > 0 ? events[0].date : dateShipped;
        const startDateTime = new Date(startDate);
        const endDateTime   = new Date(finalDate);
        const currentTime   = new Date();

        // 6. Calculate elapsed time vs. total time (from start to end)
        const totalTime   = endDateTime - startDateTime;
        const elapsedTime = currentTime - startDateTime;

        // If we're not yet past the start, clamp at 0% (i.e., "left").
        // If we've gone beyond the end, clamp at 100% (but not yet delivered).
        let percentageComplete = (elapsedTime / totalTime) * 100;
        percentageComplete = Math.max(0, Math.min(percentageComplete, 100));

        // Round to two decimal places if desired
        const result = Math.round(percentageComplete * 100) / 100;

        // 7. Decide boundary conditions:
        //    - If nearly done, call it "right."
        //    - If barely started, call it "left."
        if (result > 96) return 'right';
        if (result < 4)  return 'left';

        // 8. Otherwise, just return the calculated percentage
        return result;
    }

    /**
     * Returns a standardized object for various shipping statuses.
     *
     * @param {string} status - The shipping status (lowercase expected, but function is case-insensitive).
     * @param {string} [datetime] - An optional ISO date string (e.g. "2025-01-06T10:30:00Z").
     *
     * @returns {{ text: string, icon: string, message: string, borderStyle: string }}
     *
     * Example output:
     * {
     *   text: "On the Way",
     *   icon: "ShipStatusInTransit",
     *   message: "Your package is on its way." // or "Your package should arrive by 10:30AM on Jan 6, 2025."
     *   borderStyle: "border-l-[10px] border-blue"
     * }
     */
    function getStatusResponse(status, datetime) {
        // Helper function to check if the time is exactly midnight in UTC
        const isEndOfDay = (isoDate) => {
            const parsedDate = DateTime.fromISO(isoDate, { setZone: true }); // Parse as UTC
            return parsedDate.hour === 0 && parsedDate.minute === 0 && parsedDate.second === 0;
        };

        // Format the response based on the presence and content of datetime
        const formattedArrival = datetime
            ? isEndOfDay(datetime)
                ? `Your package should arrive by the end of day on ${DateTime.fromISO(datetime, { setZone: true }).toFormat('DDD')}.`
                : `Your package should arrive by ${DateTime.fromISO(datetime, { setZone: true }).toFormat('hh:mma')} on ${DateTime.fromISO(datetime, { setZone: true }).toFormat('DDD')}.`
            : "Your package is on its way.";

        // Normalize input to lowercase for matching
        const s = status?.toLowerCase() || "";

        // FIRST: Check for partial matches like "processed at ..."
        if (s.includes("processed at")) {
            return {
                text: "Processed at Gateway",
                icon: "ShipStatusDepartedFacility",
                message: formattedArrival,
                borderStyle: "border-l-[10px] border-blue",
            };
        }

        if (s.includes("delay")) {
            return {
                text: "Delayed",
                icon: "ShipStatusException",
                message: `Your shipment has experienced a delay. Please check the carrier's website for more details.`,
                borderStyle: "border-l-[10px] border-amber",
            };
        }

        switch (s) {
            /********************************************/
            /*         FEDEX STATUSES                   */
            /********************************************/
            case "shipment information sent to fedex":
                return {
                    text: "Shipment Created",
                    icon: "ShipStatusPreShip",
                    message: "Your package label has been created by FedEx.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "in transit":
                return {
                    text: "On the Way",
                    icon: "ShipStatusInTransit",
                    message: formattedArrival,
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "at a local fedex facility":
                return {
                    text: "At Local FedEx Facility",
                    icon: "ShipStatusArrivedAtFacility",
                    message: "Your package is at a nearby FedEx facility and will continue moving soon.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "out for delivery":
                return {
                    text: "Out for Delivery",
                    icon: "ShipStatusOutForDelivery",
                    message: datetime
                        ? `Your package is out for delivery today (arriving by ${DateTime.fromISO(datetime, { setZone: true }).toFormat('hh:mma')}).`
                        : "Your package is out for delivery today.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "delivered":
                return {
                    text: "Delivered",
                    icon: "ShipStatusDelivered",
                    message: datetime
                        ? `Your package was delivered on ${DateTime.fromISO(datetime, { setZone: true }).toFormat('DDD')} at ${DateTime.fromISO(datetime, { setZone: true }).toFormat('hh:mma')}.`
                        : "Your package has been delivered.",
                    borderStyle: "border-l-[10px] border-green",
                };

            case "scheduled delivery is now pending":
                return {
                    text: "Delivery Pending",
                    icon: "ShipStatusInTransit",
                    message: "Your package delivery date is being updated. Please check back soon.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "cancelled":
                return {
                    text: "Cancelled",
                    icon: "ShipStatusException",
                    message: "This shipment was cancelled and will not be delivered.",
                    borderStyle: "border-l-[10px] border-red",
                };

            case "delivery exception":
                return {
                    text: "Delivery Exception",
                    icon: "ShipStatusException",
                    message: "An issue occurred during delivery. Please contact support for more info.",
                    borderStyle: "border-l-[10px] border-red",
                };

            case "delivery is held, unable to collect payment":
                return {
                    text: "Payment Issue",
                    icon: "ShipStatusException",
                    message: "Delivery is on hold due to unpaid fees. Please contact FedEx to resolve payment.",
                    borderStyle: "border-l-[10px] border-red",
                };

            case "clearance delay":
                return {
                    text: "Clearance Delay",
                    icon: "ShipStatusException",
                    message: "Your package is delayed in customs clearance.",
                    borderStyle: "border-l-[10px] border-amber",
                };

            case "operational delay":
                return {
                    text: "Operational Delay",
                    icon: "ShipStatusException",
                    message: "Operational delays are causing a late delivery. Please check back for updates.",
                    borderStyle: "border-l-[10px] border-amber",
                };

            case "weather delay":
                return {
                    text: "Weather Delay",
                    icon: "ShipStatusException",
                    message: "Severe weather conditions are causing a delay. Your package will be delivered as soon as possible.",
                    borderStyle: "border-l-[10px] border-amber",
                };

            case "local delay":
                return {
                    text: "Local Delay",
                    icon: "ShipStatusException",
                    message: "A local event or issue is causing a delay. Delivery will resume shortly.",
                    borderStyle: "border-l-[10px] border-amber",
                };

            /********************************************/
            /*            UPS STATUSES                  */
            /********************************************/
            case "label created":
                return {
                    text: "Shipment Created",
                    icon: "ShipStatusLabelCreated",
                    message: "Your package label has been created by UPS.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "shipped/on the way":
                return {
                    text: "On the Way",
                    icon: "ShipStatusOutForDelivery",
                    message: formattedArrival,
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "loaded on delivery vehicle":
                return {
                    text: "Loaded on Delivery Vehicle",
                    icon: "ShipStatusOutForDelivery",
                    message: formattedArrival,
                    borderStyle: "border-l-[10px] border-blue",
                };

            // "On the way" is already handled above if we want to unify them,
            // but we can keep them separate if needed:
            case "on the way":
                return {
                    text: "On the Way",
                    icon: "ShipStatusOutForDelivery",
                    message: formattedArrival,
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "delivered to a ups access point":
                return {
                    text: "Delivered (UPS Access Point)",
                    icon: "ShipStatusAvailableForPickup",
                    message: "Your package has been delivered to a UPS Access Point for pickup.",
                    borderStyle: "border-l-[10px] border-green",
                };

            case "transferred to post office for delivery":
                return {
                    text: "Transferred to Post Office",
                    icon: "ShipStatusHeldAtPostOffice",
                    message: "UPS has transferred your package to the local post office for final delivery.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "exception":
                return {
                    text: "Exception",
                    icon: "ShipStatusException",
                    message: "A delivery exception occurred. Please contact UPS or check tracking for details.",
                    borderStyle: "border-l-[10px] border-red",
                };

            /********************************************/
            /*            DHL STATUSES                  */
            /********************************************/
            // ---- Informational Status - No Action Required ----
            case "shipment information received":
                return {
                    text: "Shipment Information Received",
                    icon: "ShipStatusLabelCreated",
                    message: "A DHL waybill was created, and shipment info was received.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "supplementary information has been received and merged with applicable shipment data":
                return {
                    text: "Additional Shipment Data Received",
                    icon: "ShipStatusInTransit",
                    message: "Extra details have been merged with your DHL shipment.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "arrived at sort facility":
                return {
                    text: "At DHL Sort Facility",
                    icon: "ShipStatusInTransit",
                    message: "Your package is moving through a DHL sorting center.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "departed facility":
                return {
                    text: "Departed DHL Facility",
                    icon: "ShipStatusDepartedFacility",
                    message: "Your package left the DHL facility en route to the next location.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "clearance processing complete":
                return {
                    text: "Clearance Complete",
                    icon: "ShipStatusCustomsClearance",
                    message: "Customs clearance has been completed successfully.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "customs clearance status updated. note - the customs clearance process may start while the shipment is in transit to the destination.":
                return {
                    text: "Clearance Complete",
                    icon: "ShipStatusCustomsClearance",
                    message: "Customs clearance has been completed successfully.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "transfer through":
                return {
                    text: "Transfer Through DHL Gateway",
                    icon: "ShipStatusInTransit",
                    message: "Your package is transferring through a DHL gateway.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "arrived at delivery facility":
                return {
                    text: "Arrived at Delivery Facility",
                    icon: "ShipStatusArrivedAtFacility",
                    message: "Your package is at the DHL delivery facility in the destination country.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "preparing for delivery":
                return {
                    text: "Preparing for Delivery",
                    icon: "ShipStatusOutForDelivery",
                    message: datetime
                        ? `Your package is out for delivery today (arriving by ${DateTime.fromISO(datetime, { setZone: true }).toFormat('hh:mma')}).`
                        : "Your package is out for delivery today.",
                    borderStyle: "border-l-[10px] border-blue",
                }

            case "shipment is out with courier for delivery":
            case "shipment out for delivery":
                return {
                    text: "Out for Delivery",
                    icon: "ShipStatusOutForDelivery",
                    message: datetime
                        ? `Your package is out for delivery today (arriving by ${DateTime.fromISO(datetime, { setZone: true }).toFormat('hh:mma')}).`
                        : "Your package is out for delivery today.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "payment is received and recorded for shipment related fees":
                return {
                    text: "Payment Received",
                    icon: "ShipStatusInTransit",
                    message: "DHL has recorded your shipment fees payment.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "scheduled to depart on the next planned movement":
                return {
                    text: "Scheduled to Depart",
                    icon: "ShipStatusInTransit",
                    message: "Your package is awaiting its next flight or transport out of the DHL gateway.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            // ---- Notification Status - Optional Actions Available ----
            case "shipment picked up":
                return {
                    text: "Shipment Picked Up",
                    icon: "ShipStatusInTransit",
                    message: formattedArrival,
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "broker has been notified to arrange clearance":
                return {
                    text: "Broker Notified",
                    icon: "ShipStatusInTransit",
                    message: "A customs broker has been alerted to clear your shipment.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "processed for clearance":
                return {
                    text: "Processed for Clearance",
                    icon: "ShipStatusInTransit",
                    message: "DHL is reviewing required documents for customs clearance.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "shipment will be cleared and delivered by broker":
                return {
                    text: "Broker Handling Clearance & Delivery",
                    icon: "ShipStatusInTransit",
                    message: "Your shipment is with the appointed broker for clearance and final delivery.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "scheduled for delivery":
                return {
                    text: "Scheduled for Delivery",
                    icon: "ShipStatusInTransit",
                    message: datetime
                        ? `Your package is scheduled for delivery on ${DateTime.fromISO(datetime, { setZone: true }).toFormat('DDD')}.`
                        : "Your package is scheduled for delivery soon.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            case "delivery has been arranged through a third-party service. delivery details are not expected":
                return {
                    text: "Third-Party Delivery",
                    icon: "ShipStatusInTransit",
                    message: "A third-party service is handling the last mile. No further details are provided.",
                    borderStyle: "border-l-[10px] border-blue",
                };

            // ---- Alert Status - Immediate Attention Required ----
            case "awaiting collection by consignee":
                return {
                    text: "Awaiting Pickup",
                    icon: "ShipStatusReadyForPickup",
                    message: "Your package is waiting for pickup by the recipient at the local DHL facility.",
                    borderStyle: "border-l-[10px] border-amber",
                };

            case "shipment is on hold":
                return {
                    text: "On Hold",
                    icon: "ShipStatusHeldAtPostOffice",
                    message: "Your package is on hold. Please contact DHL for more information.",
                    borderStyle: "border-l-[10px] border-red",
                };

            case "delivery attempt could not be completed":
                return {
                    text: "Delivery Attempt Failed",
                    icon: "ShipStatusDeliveryAttempted",
                    message: "DHL attempted delivery but could not complete it. Please contact DHL.",
                    borderStyle: "border-l-[10px] border-amber",
                };

            case "delivery attempted but no response at consignee address":
                return {
                    text: "No Response at Delivery",
                    icon: "ShipStatusDeliveryAttempted",
                    message: "DHL attempted delivery, but no one was available. Contact DHL to reschedule.",
                    borderStyle: "border-l-[10px] border-amber",
                };

            case "returned to shipper":
            case "returned to sender":
                return {
                    text: "Returned to Sender",
                    icon: "ShipStatusReturnToSender",
                    message: "Your package is being returned to the original sender. Please contact support.",
                    borderStyle: "border-l-[10px] border-amber",
                };

            case "further consignee information needed":
                return {
                    text: "Additional Info Needed",
                    icon: "ShipStatusException",
                    message: "Your package requires more recipient details. Please contact DHL.",
                    borderStyle: "border-l-[10px] border-red",
                };

            case "on hold awaiting for payment of shipment related fees":
                return {
                    text: "On Hold (Payment Required)",
                    icon: "ShipStatusException",
                    message: "Your package is on hold due to unpaid import fees. Please contact DHL to pay.",
                    borderStyle: "border-l-[10px] border-red",
                };

            /********************************************/
            /*          DEFAULT / UNKNOWN               */
            /********************************************/
            default:
                return {
                    text: "Details Unavailable",
                    icon: "ShipStatusNoAccessToLoc",
                    message: "Please check your tracking details or contact support for more information.",
                    borderStyle: "border-l-[10px] border-gray-325",
                };
        }
    }

    return {
        isLoading,
        shipments,
        getDeliveryStatusHeader,
        fetchTrackingInformation,
        getPercentageComplete,
        getStatusResponse,
    }
}
